/**
 * CSRF Token Utilities
 *
 * Provides utilities for extracting and managing CSRF tokens from cookies.
 * The CSRF token is set by the backend during authentication and must be
 * included in the X-CSRF-Token header for all mutating requests.
 *
 * Security Pattern: Double-submit cookie pattern
 * - Backend sets CSRF token in non-HttpOnly cookie (readable by JavaScript)
 * - Frontend reads token from cookie and sends in X-CSRF-Token header
 * - Backend validates that cookie value matches header value
 *
 * Requirements: 1.8 (CSRF Protection)
 */

/**
 * Extract CSRF token from cookies.
 *
 * The backend sets the CSRF token in a cookie named 'csrf_token'.
 * This function reads that cookie value for inclusion in request headers.
 *
 * @param cookieName - Name of the CSRF cookie (default: 'csrf_token')
 * @returns The CSRF token value, or null if not found
 */
export function getCsrfTokenFromCookie(cookieName: string = "csrf_token"): string | null {
  // Parse document.cookie into key-value pairs
  const cookies = document.cookie.split(";").reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split("=");
    if (key && value) {
      acc[key] = decodeURIComponent(value);
    }
    return acc;
  }, {} as Record<string, string>);

  const token = cookies[cookieName] || null;
  console.log('[CSRF] Getting CSRF token from cookie:', {
    cookieName,
    allCookies: Object.keys(cookies),
    token: token ? `${token.substring(0, 10)}...` : null,
    documentCookie: document.cookie.substring(0, 100)
  });

  return token;
}

/**
 * Check if an HTTP method requires CSRF protection.
 *
 * CSRF protection is required for state-changing operations.
 * GET, HEAD, and OPTIONS requests are considered safe and don't require CSRF tokens.
 *
 * @param method - HTTP method (e.g., 'GET', 'POST', 'PUT', 'PATCH', 'DELETE')
 * @returns True if the method requires CSRF protection
 */
export function requiresCsrfToken(method: string): boolean {
  const safeMethods = ["GET", "HEAD", "OPTIONS"];
  return !safeMethods.includes(method.toUpperCase());
}

/**
 * Get CSRF token for request headers.
 *
 * Combines cookie extraction with method checking to determine
 * if and when to include the CSRF token in request headers.
 *
 * @param method - HTTP method of the request
 * @returns CSRF token if required and available, null otherwise
 */
export function getCsrfTokenForRequest(method: string): string | null {
  if (!requiresCsrfToken(method)) {
    return null;
  }

  return getCsrfTokenFromCookie();
}
